/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description ArrayTable with Expandable support
 */
import React, { useState, useMemo, useEffect } from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import { Button } from 'antd';
import { RecursionField, useField, useFieldSchema, Schema } from '@formily/react';
import { observer } from '@formily/reactive-react';
import { ObjectField } from '@formily/core';
import { ArrayTable } from '@formily/antd-v5';
import { Down, Right } from '@icon-park/react';

// Expandable Component for ArrayTable
export const Expandable: React.FC<AnyType> = observer(() => {
	return <></>;
});

Expandable.displayName = 'ArrayTable.Expandable';

// Custom ArrayTable with Expandable support
const CustomArrayTable: React.FC<AnyType> & {
	Column?: typeof ArrayTable.Column;
	Addition?: typeof ArrayTable.Addition;
	Remove?: typeof ArrayTable.Remove;
	MoveDown?: typeof ArrayTable.MoveDown;
	MoveUp?: typeof ArrayTable.MoveUp;
	SortHandle?: typeof ArrayTable.SortHandle;
	Index?: typeof ArrayTable.Index;
	useArray?: typeof ArrayTable.useArray;
	useIndex?: typeof ArrayTable.useIndex;
	useRecord?: typeof ArrayTable.useRecord;
	Expandable?: typeof Expandable;
} = observer((props) => {
	const field = useField<ObjectField>();
	const schema = useFieldSchema();
	const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

	// Find expandable schema (search under items where ArrayTable columns live)
	const expandableSchema = useMemo(() => {
		const container: AnyType = (schema as AnyType)?.items ?? schema;
		if (!container?.reduceProperties) {
			return null;
		}
		return container.reduceProperties(
			(buf: Schema | null, s: Schema) => {
				if (s?.['x-component'] === 'ArrayTable.Expandable') {
					return s;
				}
				return buf;
			},
			null as Schema | null,
		);
	}, [schema]);

	const outerExpandableProps = props?.expandableProps || {};
	const childExpandableProps = expandableSchema?.['x-component-props'] || {};
	const expandableProps = { ...childExpandableProps, ...outerExpandableProps };
	const { contentClassName, ...expandableConfig } = expandableProps || {};

	// Custom expanded row render function
	const expandedRowRender = (__: AnyType, index: number) => {
		if (!expandableSchema) {
			return null;
		}

		const expandableContent = expandableSchema.reduceProperties(
			(buf, s, key) => {
				return [...buf, { schema: s, key }];
			},
			[] as { schema: Schema; key: string }[],
		);

		if (expandableContent.length === 0) {
			return null;
		}

		const basePath = field.address.concat(index);

		return (
			<div className={clsx('array-table-expanded-content', contentClassName)}>
				{expandableContent.map(({ schema: itemSchema, key }) => (
					<RecursionField key={key} basePath={basePath} schema={itemSchema} />
				))}
			</div>
		);
	};

	const defaultExpandIcon = (iconProps: AnyType) => {
		const { expanded, onExpand, record, expandable } = iconProps;
		if (!expandable) {
			return <span className='inline-block' />;
		}
		return <Button size='small' onClick={(e) => onExpand(record, e)} icon={expanded ? <Down /> : <Right />} />;
	};

	useEffect(() => {
		const defaults = expandableProps?.defaultExpandedRowKeys;
		if (_.isArray(defaults)) {
			setExpandedRowKeys(defaults);
		}
	}, [expandableProps?.defaultExpandedRowKeys]);

	// compute hidden for Expandable
	const isExpandableHidden = useMemo(() => {
		const hidden = expandableSchema?.['x-hidden'];
		if (typeof hidden === 'boolean') {
			return hidden;
		}
		if (typeof hidden === 'string' && hidden.includes('$form.readPretty')) {
			return !!field.form?.readPretty;
		}
		return false;
	}, [expandableSchema, field.form?.readPretty]);

	// only attach expandable when not hidden
	const finalExpandableConfig =
		expandableSchema && !isExpandableHidden
			? {
					expandedRowKeys,
					onExpandedRowsChange: (keys: React.Key[]) => setExpandedRowKeys(keys),
					expandedRowRender,
					...expandableConfig,
					expandIcon: expandableProps.expandIcon ?? defaultExpandIcon,
				}
			: undefined;

	// Pass modified props to original ArrayTable
	const arrayTableProps = {
		...props,
		// ensure stable keys for expansion unless caller provides one
		rowKey: props?.rowKey ?? ((__: AnyType, idx: number) => idx),
		expandable: finalExpandableConfig,
	};

	return <ArrayTable {...arrayTableProps} />;
});

// Copy static properties from original ArrayTable
CustomArrayTable.Column = ArrayTable.Column;
CustomArrayTable.Addition = ArrayTable.Addition;
CustomArrayTable.Remove = ArrayTable.Remove;
CustomArrayTable.MoveDown = ArrayTable.MoveDown;
CustomArrayTable.MoveUp = ArrayTable.MoveUp;
CustomArrayTable.SortHandle = ArrayTable.SortHandle;
CustomArrayTable.Index = ArrayTable.Index;
CustomArrayTable.useArray = ArrayTable.useArray;
CustomArrayTable.useIndex = ArrayTable.useIndex;
CustomArrayTable.useRecord = ArrayTable.useRecord;
CustomArrayTable.Expandable = Expandable;

export default CustomArrayTable;
